import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Camera, FileText, ClipboardList } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Empty } from '@/components/ui/empty';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import { useAcordos } from '@/lib/hooks/entidades';
import { OcrCaptureFlow, type OcrDocType } from '@/features/prestacoes/OcrCaptureFlow';

export const Route = createFileRoute('/_app/prestacoes/ocr-capture')({
  component: OcrCapturePage,
});

const DOC_TYPE_LABEL: Record<OcrDocType, string> = {
  NFE: 'Nota Fiscal (NF-e / NFS-e)',
  RECIBO: 'Recibo de pagamento',
  BOLETO: 'Boleto bancário',
  EXTRATO: 'Extrato bancário',
};

function defaultCompetencia(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function OcrCapturePage() {
  const auth = useAuth();
  const workspaceId = auth.user?.effectiveWorkspaceId ?? null;
  const acordosQuery = useAcordos(workspaceId);

  const [acordoId, setAcordoId] = useState('');
  const [competencia, setCompetencia] = useState(defaultCompetencia());
  const [docType, setDocType] = useState<OcrDocType>('NFE');

  // Regra de produto: NFE = documento_fiscal; demais = pagamento.
  // (Pode ser alterado depois pelo usuário na fila de revisão se necessário.)
  const kind = useMemo<'pagamento' | 'documento_fiscal'>(
    () => (docType === 'NFE' ? 'documento_fiscal' : 'pagamento'),
    [docType],
  );

  const validCompetencia = /^\d{4}-\d{2}$/.test(competencia);
  const canCapture = workspaceId && !!acordoId && validCompetencia;

  const acordos = acordosQuery.data?.items ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 sm:p-6">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <Camera className="h-5 w-5 text-accent" strokeWidth={1.8} />
          <h1 className="font-display text-[22px] font-bold tracking-[-0.015em] text-fg">
            Captura de documento
          </h1>
        </div>
        <p className="text-sm text-fg-muted">
          Tire foto ou selecione um arquivo de documento fiscal, recibo, boleto ou extrato.
          A IA extrai os dados automaticamente. Se a confiança ficar abaixo de 85%, vai pra fila de
          revisão antes de entrar na prestação.
        </p>
      </header>

      <Card className="p-4 sm:p-5 space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="acordo">Convênio</Label>
            <Select value={acordoId} onValueChange={setAcordoId}>
              <SelectTrigger id="acordo" disabled={!acordos.length}>
                <SelectValue
                  placeholder={
                    acordosQuery.isLoading
                      ? 'Carregando convênios…'
                      : acordos.length
                      ? 'Selecione o convênio'
                      : 'Nenhum convênio disponível'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {acordos.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.numero ? `${a.numero} — ` : ''}
                    {a.objeto ?? a.orgaoConcedente ?? a.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="competencia">Competência (mês)</Label>
            <Input
              id="competencia"
              type="month"
              value={competencia}
              onChange={(e) => setCompetencia(e.target.value)}
              aria-invalid={!validCompetencia}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="docType">Tipo de documento</Label>
          <Select value={docType} onValueChange={(v) => setDocType(v as OcrDocType)}>
            <SelectTrigger id="docType">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(DOC_TYPE_LABEL) as OcrDocType[]).map((t) => (
                <SelectItem key={t} value={t}>
                  {DOC_TYPE_LABEL[t]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11.5px] text-fg-muted flex items-center gap-1.5">
            <FileText className="h-3 w-3" strokeWidth={1.6} />
            Será criado como{' '}
            <strong className="text-fg">{kind === 'pagamento' ? 'pagamento' : 'documento fiscal'}</strong>{' '}
            em rascunho OCR.
          </p>
        </div>
      </Card>

      <Card className="p-4 sm:p-5">
        {!canCapture ? (
          <Empty
            icon={ClipboardList}
            title="Preencha convênio e competência"
            description="Selecione o convênio e o mês de competência para liberar a captura."
          />
        ) : (
          <OcrCaptureFlow
            workspaceId={workspaceId!}
            acordoId={acordoId}
            competencia={competencia}
            kind={kind}
            docType={docType}
          />
        )}
      </Card>

      <p className="text-[11.5px] text-fg-muted text-center">
        Documentos em revisão aparecem em{' '}
        <a href="/prestacoes" className="underline hover:text-fg">
          Prestações → Fila de revisão OCR
        </a>
        .
      </p>
    </div>
  );
}
